// AGENT-MENTION (05 §Delegation; 06 §Boards and tasks): handing a task to a digital employee
// ("Delegate to a DE") never silently does nothing. The Delegate picker says, per DE, whether it
// would start now (Ready, Paused, Not live yet, Agents off); under the row, each DE the task is
// handed to has a line from `tasks.agentWakes`: Queued, Running, Done, or why nothing started.
// The seat (responsibility) and the wake-up (execution) stay distinct: the line is the latter.
// It refetches on the task's pings (task-cache.ts: assigned, unassigned, agent_wake_changed).
import { cn } from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Api } from "../../api/client";
import { AgentWakeLine } from "../chat/agent-wakes";
import { digitalEmployeesQuery } from "../queries";
import { workKeys } from "./work-queries";

/** The Delegate picker's status of each DE seat ("Ready", "Paused", …), by seat id. */
export function useDelegateReadiness(
  api: Api,
  co: string,
  agentsOn: boolean,
): ReadonlyMap<string, string> {
  const des = useInfiniteQuery(digitalEmployeesQuery(api, co));
  const pages = des.data?.pages;
  return useMemo(() => {
    const out = new Map<string, string>();
    for (const d of pages?.flatMap((p) => p.data) ?? []) {
      if (!d.seat) continue;
      out.set(
        d.seat.id,
        d.status === "paused"
          ? "Paused"
          : d.status === "not_deployed"
            ? "Not deployed"
            : d.status === "onboarding"
              ? "Not live yet"
              : d.status === "retired"
                ? "Retired"
                : agentsOn
                  ? "Ready"
                  : "Agents off",
      );
    }
    return out;
  }, [pages, agentsOn]);
}

/** The task's handoffs, one line per DE it is handed to. */
export function TaskHandoffs({ api, co, taskId }: { api: Api; co: string; taskId: string }) {
  const wakes = useQuery({
    queryKey: workKeys.agentWakes(co, taskId),
    queryFn: () => api.client.tasks.agentWakes({ params: { co, id: taskId } }),
  });
  const rows = wakes.data?.data ?? [];
  if (!rows.length) return null;
  return (
    <div data-slot="task-handoffs" className={cn("flex min-w-0 flex-col gap-1 pb-1", "pl-[80px]")}>
      {rows.map((w) => (
        <AgentWakeLine key={`${w.membership_id}:${w.seat_id}`} wake={w} />
      ))}
    </div>
  );
}
